/**
 * What goes stale when an AI visibility report finishes.
 *
 * A finished report is the single biggest state change a property has: it
 * flips the "Run a report" onboarding step, gives the brand dashboard its
 * baseline, discovers competitor brands and satisfies workflow steps. All of
 * those queries are cached for minutes, so without an explicit invalidation
 * the app keeps showing the pre-report world long after the run is done.
 *
 * Cross-module key roots are duplicated rather than imported so this file has
 * no `apiClient` in its import graph and can be unit tested under Node. Each
 * one names its source.
 */

import type { QueryClient } from "@tanstack/react-query";

/** `aiReportListQueryKey` — ./api. */
export const AI_REPORT_KEY_ROOT = "ai-reports";

/** `propertyOnboardingStatusQueryKey` — @saas/properties/lib/api. Supplies
 *  `hasAiReports`, which is both the "Run a report" step and half of the brand
 *  dashboard's `hasBaseline` gate. Cached for five minutes. */
export const PROPERTY_ONBOARDING_STATUS_KEY_ROOT = "property-onboarding-status";

/** `discoveryMetricsHistoryQueryKey` — @saas/properties/lib/api. The other
 *  half of the brand dashboard baseline; keyed by property then by window, so
 *  only a prefix invalidation catches every window in the cache. */
export const DISCOVERY_METRICS_HISTORY_KEY_ROOT = "discovery-metrics-history";

/** `competitorsQueryKey(_, propertyId)` — @saas/competitors/lib/api. The
 *  report is what discovers a subject's competitor brands. The live key
 *  appends `activeOnly`, which a prefix invalidation still matches. */
export const COMPETITORS_KEY_ROOT = "competitors";

/** `discoveryQueryKeys.all` — @saas/discovery-queries/lib/api. A report run
 *  generates discovery prompts for the property. */
export const DISCOVERY_QUERY_KEY_ROOT = "discovery-queries";

/** @saas/workflows/lib/api — observed state and the companion's run detail. */
export const WORKFLOW_STATE_KEY_ROOT = "workflow-state";
export const WORKFLOW_RUN_KEY_ROOT = "workflow-run";
export const WORKFLOW_RUNS_KEY_ROOT = "workflow-runs";

/** The reports list itself, for one property. Mirrors `aiReportListQueryKey`. */
export function aiReportPropertyListKey(propertyId: string) {
	return [AI_REPORT_KEY_ROOT, "property", propertyId] as const;
}

/**
 * Every cache prefix that a report reaching a terminal status makes wrong.
 * Exported separately from the invalidation so it can be asserted in a test
 * without a QueryClient.
 */
export function reportDependentInvalidationKeys(
	propertyId?: string,
): readonly (readonly unknown[])[] {
	const keys: (readonly unknown[])[] = [[AI_REPORT_KEY_ROOT]];

	if (propertyId) {
		keys.push([PROPERTY_ONBOARDING_STATUS_KEY_ROOT, propertyId]);
		keys.push([DISCOVERY_METRICS_HISTORY_KEY_ROOT, propertyId]);
		keys.push([COMPETITORS_KEY_ROOT, "property", propertyId]);
		keys.push([WORKFLOW_STATE_KEY_ROOT, propertyId]);
	}

	keys.push([DISCOVERY_QUERY_KEY_ROOT]);
	// Keyed by run id / org rather than by property — prefix only.
	keys.push([WORKFLOW_RUN_KEY_ROOT]);
	keys.push([WORKFLOW_RUNS_KEY_ROOT]);

	return keys;
}

/**
 * Refresh everything downstream of a report. Called once per report that
 * transitions into a terminal status, and after a report is created or
 * deleted (both change `hasAiReports`).
 */
export function invalidateReportDependents(
	queryClient: QueryClient,
	propertyId?: string,
): Promise<void> {
	return Promise.all(
		reportDependentInvalidationKeys(propertyId).map((queryKey) =>
			queryClient.invalidateQueries({ queryKey: queryKey as unknown[] }),
		),
	).then(() => undefined);
}
